import { Link } from "wouter";
import { Building2, Home, ArrowRight, TrendingUp, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { usePageTitle } from "@/hooks/use-page-title";
import { useNoindex } from "@/hooks/use-seo";
import { marketingTitle } from "@/data/marketing-seo";
import { partnerPortalUrl } from "@/lib/partner-portal-url";
import { PartnerFooter } from "@/components/partner-footer";

const partnerTypes = [
  {
    icon: Building2,
    color: "bg-blue-100 text-blue-600",
    title: "Lenders",
    description: "Get listed on the PadPulse certified lender page and put your loan products in front of active co-living investors. Pay a referral fee only when a borrower closes with you.",
    benefits: ["Featured on the certified lender page", "Unlimited borrower leads — pay only on closed deals", "Also a PadPulse affiliate"],
    applyHref: "/partners/lenders",
    disabled: false,
  },
  {
    icon: TrendingUp,
    color: "bg-purple-100 text-purple-600",
    title: "Wholesalers",
    description: "Your deals are auto-added to our certified-by page and instantly matched to every host whose buy box they fit. Pay a referral fee only when an assignment closes.",
    benefits: ["Deals auto-listed to PadPulse buyers", "Auto buy-box matching & notifications", "Airtable integration — coming Q3"],
    applyHref: "/partners/wholesalers",
    disabled: false,
  },
  {
    icon: Handshake,
    color: "bg-amber-100 text-amber-600",
    title: "Brokers",
    description: "Receive referrals from PadPulse closing teams and track every loan deal from approval to close. Commission visibility at every stage.",
    benefits: ["Referrals from PadPulse closing teams", "Deal tracking through approval & close", "Commission visibility at every stage"],
    applyHref: "/partners/brokers",
    disabled: false,
  },
  {
    icon: Home,
    color: "bg-green-100 text-green-600",
    title: "Realtors",
    description: "For a simple marketing fee, get in front of active PadPulse buyers and list your properties in the PadPulse Marketplace.",
    benefits: ["Access to PadPulse buyers", "List in the PadPulse Marketplace", "Also a PadPulse affiliate"],
    applyHref: "/partners/realtors",
    disabled: false,
  },
];

export default function PartnersHubPage() {
  usePageTitle(marketingTitle("Partners"), true);
  // The partner program is frozen (build F8): pages stay, but are noindexed and unlinked.
  useNoindex();
  const portalUrl = partnerPortalUrl();
  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
            <span className="text-white font-bold text-sm">P</span>
          </div>
          <span className="font-bold text-slate-900">PadPulse</span>
        </Link>
        <a
          href={portalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:underline font-medium"
        >
          Partner portal →
        </a>
      </header>

      <section className="bg-gradient-to-br from-slate-900 to-blue-900 text-white px-6 py-20 text-center">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block bg-blue-500/20 text-blue-200 text-sm font-medium px-4 py-1 rounded-full mb-4">
            Partner Program
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Grow your business with PadPulse
          </h1>
          <p className="text-xl text-slate-300 mb-8">
            Join our partner network to get in front of active co-living investors — get listed, get leads, and grow your business with PadPulse.
          </p>
          <p className="text-sm text-slate-400">
            Choose the program that fits your business below.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {partnerTypes.map((type) => (
            <Card
              key={type.title}
              className={`h-full border ${type.disabled ? "border-slate-200 opacity-70" : "border-slate-200 hover:border-blue-300 hover:shadow-md transition-all"}`}
              data-testid={`card-partner-type-${type.title.toLowerCase()}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className={`w-12 h-12 rounded-xl ${type.color} flex items-center justify-center mb-3`}>
                    <type.icon className="w-6 h-6" />
                  </div>
                  {type.disabled && (
                    <Badge variant="secondary" className="text-xs mt-1">Coming soon</Badge>
                  )}
                </div>
                <CardTitle className="text-xl">{type.title}</CardTitle>
                <CardDescription className="text-sm text-slate-500 leading-relaxed">
                  {type.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <ul className="space-y-1 mb-2">
                  {type.benefits.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
                {type.disabled ? (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled className="flex-1 text-xs">Log in</Button>
                    <Button size="sm" disabled className="flex-1 text-xs">Apply</Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 text-xs"
                      asChild
                      data-testid={`button-login-${type.title.toLowerCase()}`}
                    >
                      <a href={portalUrl} target="_blank" rel="noopener noreferrer">
                        Log in
                      </a>
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 text-xs bg-blue-600 hover:bg-blue-700"
                      asChild
                      data-testid={`button-apply-${type.title.toLowerCase()}`}
                    >
                      <Link href={type.applyHref!}>
                        Learn more <ArrowRight className="w-3 h-3 ml-1" />
                      </Link>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 px-6 py-16 text-center">
        <div className="max-w-xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 mb-3">Already a partner?</h2>
          <p className="text-slate-500 mb-6">Sign in to your partner portal to view your leads, messages, and account settings.</p>
          <Button asChild className="bg-blue-600 hover:bg-blue-700">
            <a href={portalUrl} target="_blank" rel="noopener noreferrer" data-testid="button-partner-portal-signin">
              Sign in to Partner Portal <ArrowRight className="w-4 h-4 ml-2" />
            </a>
          </Button>
        </div>
      </section>

      <PartnerFooter />
    </div>
  );
}
